export interface SensorRow {
  id: string;
  sensorId: number;
  sensorName: string;
  customSensorName?: string;
  inspected: boolean;
  cleaned: boolean;
  calibrated: boolean;
  replaced: boolean;
  installed: boolean;
  notes: string;
}

export interface ReportAttachment {
  uuid: string;
  filename: string;
  mimeType: string;
  url: string | null;
  isAdminOnly?: boolean;
}

export interface ReportDetail {
  uuid: string;
  stationId: number | null;
  stationName: string | null;
  organizationName: string | null;
  reportType: string;
  status: "draft" | "finished";
  responsiblePerson: string | null;
  timestamp: string | null;
  createdAt: string;
  updatedAt: string;
  data: {
    addAnnotation?: boolean;
    sensorRows?: SensorRow[];
    batteryVoltageChecked?: boolean;
    batteryChanged?: boolean;
    moistureBagChecked?: boolean;
    dataTransferChecked?: boolean;
    otherNotesPublic?: string;
    otherNotesAdmin?: string;
  };
  attachments: ReportAttachment[];
}

export const REPORT_TYPES = [
  "installation",
  "scheduledMaintenance",
  "faultMaintenance",
  "inspection",
  "other",
] as const;

export type ReportType = (typeof REPORT_TYPES)[number];

export interface FormData {
  stationId: number | null;
  stationName: string;
  organizationName: string;
  reportType: ReportType | "";
  timestamp: string;
  responsiblePerson: string;
  addAnnotation: boolean;
  sensorRows: SensorRow[];
  batteryVoltageChecked: boolean;
  batteryChanged: boolean;
  moistureBagChecked: boolean;
  dataTransferChecked: boolean;
  otherNotesPublic: string;
  otherNotesAdmin: string;
  attachments: ReportAttachment[];
}

export const generateId = (): string => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  const bytes = new Uint8Array(16);
  if (typeof crypto !== "undefined" && crypto.getRandomValues) {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256);
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = [...bytes].map((b) => b.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

export const toLocalDatetimeLocal = (utcIsoString: string | null): string => {
  if (!utcIsoString) {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    const hours = String(now.getHours()).padStart(2, "0");
    const minutes = String(now.getMinutes()).padStart(2, "0");
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  }
  const date = new Date(utcIsoString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

export const toUtcIsoString = (localDatetimeLocal: string): string => {
  if (!localDatetimeLocal) return "";
  const date = new Date(localDatetimeLocal);
  return date.toISOString();
};
